import { FC } from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { colors, fontWeights, radii } from '../../theme/tokens';
import PageThumbnail from './PageThumbnail';
import { PageRow } from './types';

// The step 1 page list (ROADMAP.md, Part 3): thumbnail, "Page N",
// name and size, ↑↓ arrows and a trash can. Reordering with arrows, not by
// dragging: the same on mobile and without the accessibility problem of dragging.
//
// An uploading row shows its progress; a failed one, the reason in French
// with "Réessayer" and "Retirer". While an upload is in progress, moving and
// deleting are inactive: the server positions are changing.
//
// `minPages`: below that number the trash can is inactive. The
// editing of an archived document sets it to 1, so that it is not left without
// pages (a document without pages can no longer be reassembled).

type Props = {
  rows: PageRow[];
  disabled: boolean;
  minPages?: number;
  onMove: (pageId: string, direction: -1 | 1) => void;
  onRemove: (pageId: string) => void;
  onRetry: (key: string) => void;
  onDismiss: (key: string) => void;
};

export const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${bytes} o`;
};

const MIME_LABEL: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPEG',
  'image/png': 'PNG',
};

const PageList: FC<Props> = ({
  rows,
  disabled,
  minPages = 0,
  onMove,
  onRemove,
  onRetry,
  onDismiss,
}) => {
  const pageRows = rows.filter((row) => row.kind === 'page');
  const atMinimum = pageRows.length <= minPages;

  return (
    <Box
      component="ol"
      aria-label="Pages du document"
      sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      {rows.map((row, index) => {
        const isPage = row.kind === 'page';
        const number = index + 1;
        const serverIndex = isPage ? pageRows.indexOf(row) : -1;

        return (
          <Box
            component="li"
            key={row.key}
            data-testid="page-row"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto minmax(0, 1fr) auto',
              alignItems: 'center',
              columnGap: 1.5,
              p: 1,
              borderRadius: `${radii.card}px`,
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              border: `1px solid ${
                row.kind === 'upload' && row.error
                  ? colors.accentLight
                  : 'rgba(255, 255, 255, 0.16)'
              }`,
            }}
          >
            <PageThumbnail
              page={isPage ? row.page : undefined}
              alt={`Page ${number}`}
              size={48}
            />

            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  component="span"
                  sx={{ fontWeight: fontWeights.semiBold, whiteSpace: 'nowrap' }}
                >
                  Page {number}
                </Typography>
                {isPage && row.page.verification_url && (
                  <Tooltip title="QR code de vérification lu sur cette page">
                    <QrCode2Icon
                      fontSize="small"
                      aria-label="QR code détecté"
                      sx={{ color: colors.accentLight }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  opacity: 0.85,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isPage
                  ? [
                      row.fileName ?? MIME_LABEL[row.page.mime_type] ?? row.page.mime_type,
                      formatBytes(row.page.byte_size),
                    ].join(' · ')
                  : [row.fileName, formatBytes(row.byteSize)].join(' · ')}
              </Typography>

              {row.kind === 'upload' && !row.error && (
                <LinearProgress
                  aria-label={`Envoi de la page ${number}`}
                  variant={row.progress === null ? 'indeterminate' : 'determinate'}
                  value={row.progress === null ? undefined : Math.round(row.progress * 100)}
                  color="secondary"
                  sx={{
                    mt: 0.75,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.24)',
                  }}
                />
              )}

              {row.kind === 'upload' && row.error && (
                <Typography
                  role="alert"
                  sx={{ mt: 0.5, fontSize: '0.8125rem', color: colors.accentLight }}
                >
                  {row.error}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isPage ? (
                <>
                  <IconButton
                    aria-label={`Monter la page ${number}`}
                    size="small"
                    disabled={disabled || serverIndex <= 0}
                    onClick={() => onMove(row.page.id, -1)}
                    sx={{ color: colors.white, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label={`Descendre la page ${number}`}
                    size="small"
                    disabled={disabled || serverIndex >= pageRows.length - 1}
                    onClick={() => onMove(row.page.id, 1)}
                    sx={{ color: colors.white, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <Tooltip
                    title={
                      atMinimum
                        ? `Un document conserve au moins ${minPages} page${minPages > 1 ? 's' : ''}`
                        : ''
                    }
                  >
                    <span>
                      <IconButton
                        aria-label={`Supprimer la page ${number}`}
                        size="small"
                        disabled={disabled || atMinimum}
                        onClick={() => onRemove(row.page.id)}
                        sx={{ color: colors.white, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : row.error ? (
                <>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => onRetry(row.key)}
                    sx={{ color: colors.accentLight, px: 1, py: 0.25, minWidth: 0 }}
                  >
                    Réessayer
                  </Button>
                  <IconButton
                    aria-label={`Retirer ${row.fileName}`}
                    size="small"
                    onClick={() => onDismiss(row.key)}
                    sx={{ color: colors.white }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default PageList;
